import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Plus, ShoppingBag } from "lucide-react";
import {
  BORROWER_TYPES, BorrowerType, ClientRecord, Item, Loan, LocationRecord,
  RETURN_CONDITIONS, ReturnCondition, clientsApi, dueDescriptor, fileUrl,
  itemsApi, loansApi, locationsApi,
} from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui-shim";
import { Skeleton } from "@/components/catalog/Skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui-shim";
import { toast } from "sonner";

type Tab = "active" | "overdue" | "returned" | "all";

export default function Loans() {
  const [tab, setTab] = useState<Tab>("active");
  const [loans, setLoans] = useState<Loan[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [locations, setLocations] = useState<LocationRecord[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [returnTarget, setReturnTarget] = useState<Loan | null>(null);

  async function load() {
    const params: Record<string, any> = {};
    if (tab === "active") params.status = "Active";
    else if (tab === "overdue") params.overdue = true;
    else if (tab === "returned") params.status = "Returned";
    setLoans(null);
    try { setLoans(await loansApi.list(params)); }
    catch { setLoans([]); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    itemsApi.list({ page_size: 100 }).then((r) => setItems(r.items)).catch(() => {});
    clientsApi.list().then(setClients).catch(() => {});
    locationsApi.list().then(setLocations).catch(() => {});
  }, []);

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="loans-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Out the door</p>
          <h1 className="font-display text-3xl sm:text-4xl">Loans &amp; returns</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-prose">
            Track every piece that's on a body, on a set, or in a courier van. Mark
            returns with condition so the next pull is honest.
          </p>
        </div>
        <Button data-testid="loan-new-btn" onClick={() => setCreateOpen(true)}
          className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 px-5">
          <Plus className="w-4 h-4 mr-2" />Log a loan
        </Button>
      </div>

      <div className="flex border-b border-border" role="tablist" aria-label="Loans status filter" data-testid="loans-tabs">
        {(["active", "overdue", "returned", "all"] as Tab[]).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            role="tab"
            aria-selected={tab === t}
            data-testid={`loans-tab-${t}`}
            className={`px-4 py-2.5 text-sm uppercase tracking-[0.2em] transition-colors -mb-px border-b-2 ${
              tab === t ? "border-oxblood text-oxblood" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {loans === null ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : loans.length === 0 ? (
          <div className="border border-border bg-bone/30 p-12 text-center" data-testid="loans-empty">
            <ShoppingBag className="w-6 h-6 mx-auto text-oxblood" strokeWidth={1.4} />
            <p className="font-display text-2xl mt-4">Nothing on loan{tab === "overdue" ? " is overdue" : ""}.</p>
            <p className="text-sm text-muted-foreground mt-2">Quiet shelves are a happy archive.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border border-y border-border" data-testid="loans-list">
            {loans.map((loan) => (
              <LoanRow key={loan._id} loan={loan} onMarkReturn={() => setReturnTarget(loan)}
                onChange={load} />
            ))}
          </ul>
        )}
      </div>

      <CreateLoanDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        items={items}
        clients={clients}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
      <ReturnLoanDialog
        loan={returnTarget}
        locations={locations}
        onClose={() => setReturnTarget(null)}
        onReturned={() => { setReturnTarget(null); load(); }}
      />
    </main>
  );
}

function LoanRow({ loan, onMarkReturn, onChange }: {
  loan: Loan; onMarkReturn: () => void; onChange: () => void;
}) {
  const due = dueDescriptor(loan.due_at);
  const tone = loan.effective_status === "Overdue" ? "bad" :
    loan.effective_status === "Returned" ? "ok" :
    loan.effective_status === "Lost" ? "bad" : due.tone;
  const pillCls =
    tone === "bad" ? "bg-oxblood text-ivory" :
    tone === "warn" ? "bg-oxblood/15 text-oxblood-deep" :
    "bg-foreground/5 text-muted-foreground";

  async function markLost() {
    if (!window.confirm(`Mark "${loan.item?.name || "item"}" as Lost? This is final.`)) return;
    try { await loansApi.lost(loan._id); toast.success("Marked Lost."); onChange(); }
    catch (e: any) { toast.error(e?.message || "Could not update."); }
  }

  return (
    <li className="grid grid-cols-[auto_1fr_auto] gap-4 px-4 py-4 items-center" data-testid={`loan-row-${loan._id}`}>
      <div className="w-12 h-14 bg-bone overflow-hidden shrink-0">
        {loan.item?.primary_photo_url ? (
          <img src={fileUrl(loan.item.primary_photo_url)} alt={loan.item.name} loading="lazy"
            className="w-full h-full object-cover" />
        ) : null}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/app/items/${loan.item_id}`} className="font-display text-base hover:text-oxblood">
            {loan.item?.name || "(deleted item)"}
          </Link>
          <span className={`text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 ${pillCls}`}
            data-testid={`loan-pill-${loan._id}`}>
            {loan.effective_status}
          </span>
          {loan.item?.internal_code && (
            <span className="text-[10px] font-mono text-muted-foreground">{loan.item.internal_code}</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground truncate mt-0.5">
          With <span className="text-foreground">{loan.borrower_name}</span>
          {loan.purpose ? <> · {loan.purpose}</> : null}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Due {new Date(loan.due_at).toLocaleDateString()} · {due.label}
          {loan.returned_at && ` · Returned ${new Date(loan.returned_at).toLocaleDateString()}`}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {loan.status === "Active" ? (
          <>
            <Button variant="outline" size="sm"
              data-testid={`loan-return-btn-${loan._id}`}
              onClick={onMarkReturn}
              className="rounded-none border-foreground/20 hover:bg-bone h-9">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark returned
            </Button>
            <button type="button" onClick={markLost}
              data-testid={`loan-lost-btn-${loan._id}`}
              className="text-xs text-muted-foreground hover:text-oxblood underline underline-offset-4">
              Lost
            </button>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">{loan.return_condition || ""}</span>
        )}
      </div>
    </li>
  );
}

function CreateLoanDialog({ open, onClose, items, clients, onCreated }: {
  open: boolean; onClose: () => void;
  items: Item[]; clients: ClientRecord[];
  onCreated: () => void;
}) {
  const [itemId, setItemId] = useState("");
  const [borrowerName, setBorrowerName] = useState("");
  const [borrowerType, setBorrowerType] = useState<BorrowerType>("Shoot");
  const [purpose, setPurpose] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setItemId(""); setBorrowerName(""); setBorrowerType("Shoot");
      setPurpose("");
      const tomorrow = new Date(Date.now() + 7 * 86400000);
      setDueAt(tomorrow.toISOString().slice(0, 10));
    }
  }, [open]);

  async function submit() {
    if (!itemId || !borrowerName.trim() || !dueAt) {
      toast.error("Item, borrower, and due date are required.");
      return;
    }
    setSubmitting(true);
    try {
      await loansApi.create({
        item_id: itemId,
        borrower_name: borrowerName.trim(),
        borrower_type: borrowerType,
        purpose: purpose.trim() || undefined,
        due_at: new Date(`${dueAt}T17:00:00`).toISOString(),
      });
      toast.success("Loan logged.");
      onCreated();
    } catch (e: any) {
      toast.error(e?.message || "Could not log loan.");
    } finally { setSubmitting(false); }
  }

  // Suggest borrower from client list as you type.
  const clientSuggestions = clients
    .filter((c) => borrowerName && c.name.toLowerCase().includes(borrowerName.toLowerCase()))
    .slice(0, 4);

  return (
    <Dialog open={open} onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent data-testid="loan-create-dialog" className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Log a loan</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Item</label>
            <select data-testid="loan-item-select"
              className="mt-1.5 w-full h-11 border border-border bg-background px-3 text-sm"
              value={itemId} onChange={(e) => setItemId(e.target.value)}>
              <option value="">Pick a piece…</option>
              {items.map((it) => (
                <option key={it._id} value={it._id}>
                  {it.internal_code} · {it.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Borrower</label>
            <Input data-testid="loan-borrower-input"
              placeholder="e.g., Vogue / Penelope Reyes"
              value={borrowerName} onChange={(e: any) => setBorrowerName(e.target.value)}
              className="mt-1.5 h-11" />
            {clientSuggestions.length > 0 && (
              <div className="mt-1 flex gap-1 flex-wrap">
                {clientSuggestions.map((c) => (
                  <button key={c._id} type="button"
                    onClick={() => setBorrowerName(c.name)}
                    className="text-[11px] px-2 py-0.5 border border-border hover:bg-bone">
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Type</label>
              <select data-testid="loan-borrower-type"
                className="mt-1.5 w-full h-11 border border-border bg-background px-3 text-sm"
                value={borrowerType} onChange={(e) => setBorrowerType(e.target.value as BorrowerType)}>
                {BORROWER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Due</label>
              <Input type="date" data-testid="loan-due-input"
                value={dueAt} onChange={(e: any) => setDueAt(e.target.value)}
                className="mt-1.5 h-11" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Purpose</label>
            <Input data-testid="loan-purpose-input"
              placeholder="March cover try-ons"
              value={purpose} onChange={(e: any) => setPurpose(e.target.value)}
              className="mt-1.5 h-11" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="loan-submit"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none">
            {submitting ? "Logging…" : <>Log loan <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnLoanDialog({ loan, locations, onClose, onReturned }: {
  loan: Loan | null;
  locations: LocationRecord[];
  onClose: () => void;
  onReturned: () => void;
}) {
  const [returnLocId, setReturnLocId] = useState("");
  const [condition, setCondition] = useState<ReturnCondition>("As sent");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loan) { setReturnLocId(""); setCondition("As sent"); setNotes(""); }
  }, [loan]);

  async function submit() {
    if (!loan) return;
    setSubmitting(true);
    try {
      await loansApi.return(loan._id, {
        return_location_id: returnLocId || undefined,
        return_condition: condition,
        return_notes: notes.trim() || undefined,
      });
      toast.success("Loan closed.");
      onReturned();
    } catch (e: any) {
      toast.error(e?.message || "Could not mark returned.");
    } finally { setSubmitting(false); }
  }

  if (!loan) return null;

  return (
    <Dialog open={!!loan} onOpenChange={(v: boolean) => !v && onClose()}>
      <DialogContent data-testid="loan-return-dialog" className="rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Mark returned</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          {loan.item?.name} — back from {loan.borrower_name}.
        </p>
        <div className="grid gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Returned to</label>
            <select data-testid="loan-return-location"
              className="mt-1.5 w-full h-11 border border-border bg-background px-3 text-sm"
              value={returnLocId} onChange={(e) => setReturnLocId(e.target.value)}>
              <option value="">Don't update location</option>
              {locations.filter((l) => !l.archived).map((l) => (
                <option key={l._id} value={l._id}>{l.name} — {l.type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Condition</label>
            <select data-testid="loan-return-condition"
              className="mt-1.5 w-full h-11 border border-border bg-background px-3 text-sm"
              value={condition} onChange={(e) => setCondition(e.target.value as ReturnCondition)}>
              {RETURN_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Notes</label>
            <Textarea data-testid="loan-return-notes"
              placeholder="Hem letting at the right cuff, send to atelier."
              value={notes} onChange={(e: any) => setNotes(e.target.value)}
              className="mt-1.5 min-h-[80px]" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="loan-return-submit"
            className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none">
            {submitting ? "Saving…" : "Confirm return"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
